<template>
  <div v-if="page" class="seo-page">
    <section class="seo-hero">
      <div class="seo-shell seo-hero-grid">
        <div>
          <nav class="seo-breadcrumbs" aria-label="Breadcrumb">
            <router-link to="/">AliTutor</router-link>
            <span aria-hidden="true">/</span>
            <span>{{ page.school || page.location || page.subject }}</span>
          </nav>
          <p class="seo-kicker">{{ kicker }}</p>
          <h1>{{ page.h1 }}</h1>
          <p class="seo-lead">{{ lead }}</p>
          <div class="seo-actions">
            <router-link class="seo-primary" to="/contact">
              Book a free consultation
              <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </router-link>
            <a class="seo-secondary" href="#topics">See what we can cover</a>
          </div>
          <div class="seo-proof" aria-label="Tutoring highlights">
            <span><i class="fas fa-circle-check" aria-hidden="true"></i> One-on-one</span>
            <span><i class="fas fa-circle-check" aria-hidden="true"></i> Online</span>
            <span><i class="fas fa-circle-check" aria-hidden="true"></i> Step-by-step</span>
          </div>
        </div>
        <aside class="seo-summary" aria-label="Tutoring summary">
          <p class="seo-summary-label">Built around your class</p>
          <strong>{{ page.courseName || page.subject }}</strong>
          <p>{{ summary }}</p>
          <router-link to="/contact">Start with a free 30-minute consultation</router-link>
        </aside>
      </div>
    </section>

    <section id="topics" class="seo-section">
      <div class="seo-shell">
        <div class="seo-section-heading">
          <p class="seo-kicker">Focused support</p>
          <h2>What we can work on together</h2>
          <p>{{ topicIntro }}</p>
        </div>
        <div class="seo-topic-grid">
          <article v-for="topic in page.topics" :key="topic" class="seo-topic-card">
            <i class="fas fa-check" aria-hidden="true"></i>
            <span>{{ topic }}</span>
          </article>
        </div>
      </div>
    </section>

    <section class="seo-section seo-section-alt">
      <div class="seo-shell seo-two-column">
        <div>
          <p class="seo-kicker">How sessions work</p>
          <h2>Understand the reasoning, not just the answer.</h2>
          <p>
            Bring the problem set, review sheet, notes, or concept that is giving you trouble.
            We identify the exact step that stopped making sense, rebuild it clearly, and then
            practice similar problems until you can do the process yourself.
          </p>
          <p>
            Tutoring is designed to support your learning. I can explain concepts, work through
            practice with you, and help you prepare for exams, but I will not complete graded work
            on your behalf.
          </p>
        </div>
        <div class="seo-process">
          <article><span>01</span><div><strong>Find the gap</strong><p>Pinpoint the concept or step causing the confusion.</p></div></article>
          <article><span>02</span><div><strong>Build the logic</strong><p>Work through it in plain language, one step at a time.</p></div></article>
          <article><span>03</span><div><strong>Practice independently</strong><p>Use new examples so the method becomes yours.</p></div></article>
        </div>
      </div>
    </section>

    <section v-if="page.school" class="seo-section">
      <div class="seo-shell seo-campus-note">
        <div>
          <p class="seo-kicker">For {{ page.school }} students</p>
          <h2>{{ campusHeading }}</h2>
          <p>{{ campusCopy }}</p>
        </div>
        <div class="seo-independent">
          <i class="fas fa-building-columns" aria-hidden="true"></i>
          <p>
            AliTutor is an independent tutoring service and is not affiliated with, endorsed by,
            or sponsored by {{ page.school }}.
          </p>
        </div>
      </div>
    </section>

    <section v-if="relatedPages.length" class="seo-section seo-related-section">
      <div class="seo-shell">
        <div class="seo-section-heading">
          <p class="seo-kicker">Keep exploring</p>
          <h2>Related tutoring options</h2>
        </div>
        <div class="seo-related-grid">
          <router-link v-for="item in relatedPages" :key="item.id" :to="item.slug" class="seo-related-card">
            <span>{{ item.course || item.subject }}</span>
            <strong>{{ item.h1 }}</strong>
            <i class="fas fa-arrow-right" aria-hidden="true"></i>
          </router-link>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <div class="seo-shell seo-faq-wrap">
        <div class="seo-section-heading">
          <p class="seo-kicker">Questions</p>
          <h2>Before your first session</h2>
        </div>
        <div class="seo-faq">
          <details open>
            <summary>What should I bring?</summary>
            <p>Bring your syllabus, notes, homework or practice set, review sheet, and any questions you already know you want to cover.</p>
          </details>
          <details>
            <summary>Can we prepare for an exam?</summary>
            <p>Yes. We can organize the topics, identify weak spots, and practice representative problems so you know what to study next.</p>
          </details>
          <details>
            <summary>Do you do the assignment for me?</summary>
            <p>No. I help you understand the material and work through the reasoning so you can complete your own graded work.</p>
          </details>
          <details>
            <summary>Where do sessions happen?</summary>
            <p>AliTutor currently emphasizes flexible one-on-one online tutoring, so you can work from wherever you focus best.</p>
          </details>
        </div>
      </div>
    </section>

    <section class="seo-closing">
      <div class="seo-shell seo-closing-inner">
        <div>
          <p class="seo-kicker seo-kicker-light">Ready when you are</p>
          <h2>Bring the confusing part. Leave with a plan.</h2>
          <p>Start with a free 30-minute consultation and tell me what course or topic you are working on.</p>
        </div>
        <router-link to="/contact">
          Book a free consultation
          <i class="fas fa-arrow-right" aria-hidden="true"></i>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from "vue";
import { seoPageMap } from "../../data/seoPages";

const props = defineProps({ pageId: { type: String, required: true } });
const page = computed(() => seoPageMap[props.pageId]);
const relatedPages = computed(() => (page.value?.related || []).map((id) => seoPageMap[id]).filter(Boolean));

const kicker = computed(() => {
  if (page.value?.course) return `${page.value.school} • ${page.value.course}`;
  if (page.value?.school) return `Independent tutoring for ${page.value.school}`;
  if (page.value?.location) return `Tutoring in ${page.value.location}`;
  return "One-on-one college tutoring";
});

const lead = computed(() => {
  const p = page.value;
  if (!p) return "";
  if (p.course) return `If ${p.course} is moving faster than the material is clicking, we can slow it down and rebuild the logic together. Sessions focus on ${p.topics.slice(0, 3).join(", ")} and the exact problems you need to understand.`;
  if (p.school) return `${p.subject} support built for college students who want patient explanations and relevant practice. Bring the part of your ${p.school} coursework that is not making sense, and we will work from there.`;
  if (p.location) return `Practical, one-on-one ${p.subject.toLowerCase()} help for students in ${p.location}, with sessions centered on the exact skills and coursework you are trying to master.`;
  return `Build real confidence in ${p.h1.toLowerCase()} with clear explanations, guided practice, and a tutoring plan that starts at your current level.`;
});

const summary = computed(() => {
  const p = page.value;
  if (!p) return "";
  if (p.course) return `Targeted support for ${p.course} ${p.courseName}, from core concepts through exam review.`;
  if (p.subject === "Math") return "Math support is limited to arithmetic through trigonometry so the tutoring offer stays focused and credible.";
  if (p.subject === "Excel") return "Practical Excel help for formulas, tables, organization, accounting work, and common college spreadsheet tasks.";
  return "Accounting support focused on understanding the process behind journal entries, statements, costing, and other core college topics.";
});

const topicIntro = computed(() => {
  const p = page.value;
  if (!p) return "";
  return p.course
    ? `These are common areas we can work through for ${p.course}. Your actual session follows your syllabus, class materials, and current questions.`
    : `Sessions can be shaped around these areas, with the pace adjusted to what you already know and what still needs work.`;
});

const campusHeading = computed(() => page.value?.course ? `Support that follows your ${page.value.course} coursework.` : `Tutoring that starts with your actual coursework.`);
const campusCopy = computed(() => {
  const p = page.value;
  if (!p?.school) return "";
  return `Bring your ${p.school} syllabus, notes, review sheet, or assigned practice. We use your course structure to decide what to prioritize while keeping the session focused on learning the underlying concepts.`;
});

function setJsonLd() {
  if (!page.value || typeof document === "undefined") return;
  const old = document.getElementById("alitutor-seo-jsonld");
  if (old) old.remove();
  const script = document.createElement("script");
  script.id = "alitutor-seo-jsonld";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.value.h1,
    description: page.value.description,
    provider: { "@type": "Person", name: "Ali Khalil", url: "https://alitutor.com/about" },
    areaServed: page.value.location || page.value.school || "Hudson Valley, NY",
    url: `https://alitutor.com${page.value.slug}`
  });
  document.head.appendChild(script);
}

onMounted(setJsonLd);
onUnmounted(() => document.getElementById("alitutor-seo-jsonld")?.remove());
</script>

<style lang="scss">
.seo-page { background: var(--paper); color: var(--ink); }
.seo-shell { width: min(1160px, calc(100% - 40px)); margin: 0 auto; }
.seo-hero { position: relative; overflow: hidden; padding: clamp(4.5rem, 8vw, 7.5rem) 0; background: var(--ink); color: #fff; }
.seo-hero::before { content: ""; position: absolute; inset: 0; opacity: .14; background-image: linear-gradient(rgba(255,255,255,.09) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.09) 1px, transparent 1px); background-size: 64px 64px; mask-image: linear-gradient(to right, #000, transparent 90%); }
.seo-hero-grid { position: relative; display: grid; grid-template-columns: 1.25fr .75fr; gap: clamp(2.5rem, 7vw, 6rem); align-items: center; }
.seo-breadcrumbs { display: flex; gap: .55rem; margin-bottom: 1.5rem; color: #8f9bb3; font-size: .75rem; font-weight: 700; }
.seo-breadcrumbs a:hover { color: #fff; }
.seo-kicker { margin: 0 0 1rem; color: var(--blue); font-size: .75rem; font-weight: 900; letter-spacing: .14em; text-transform: uppercase; }
.seo-kicker-light { color: #aebcff; }
.seo-hero h1 { max-width: 850px; margin: 0; font-size: clamp(2.7rem, 6.4vw, 6rem); line-height: .98; letter-spacing: -.055em; }
.seo-lead { max-width: 760px; margin: 1.5rem 0 0; color: #b9c3d8; font-size: clamp(1rem, 1.4vw, 1.16rem); line-height: 1.75; }
.seo-actions { display: flex; flex-wrap: wrap; gap: .9rem; margin-top: 2rem; }
.seo-primary, .seo-secondary { display: inline-flex; min-height: 52px; align-items: center; justify-content: center; border-radius: 14px; font-size: .86rem; font-weight: 900; }
.seo-primary { gap: .7rem; padding: .9rem 1.2rem; background: var(--blue); color: #fff !important; box-shadow: 0 16px 40px rgba(49,87,246,.35); }
.seo-primary:hover { background: #4167ff; transform: translateY(-2px); }
.seo-secondary { padding: .9rem 1rem; color: #dce3f2 !important; border: 1px solid rgba(255,255,255,.18); }
.seo-proof { display: flex; flex-wrap: wrap; gap: 1rem 1.4rem; margin-top: 1.7rem; color: #8f9bb3; font-size: .74rem; font-weight: 700; }
.seo-proof span { display: inline-flex; gap: .4rem; align-items: center; }
.seo-proof i { color: var(--mint); }
.seo-summary { padding: clamp(1.6rem, 3vw, 2.4rem); border: 1px solid rgba(255,255,255,.14); border-radius: 26px; background: rgba(255,255,255,.06); box-shadow: 0 28px 80px rgba(0,0,0,.24); backdrop-filter: blur(14px); }
.seo-summary-label { margin: 0 0 .7rem; color: var(--amber); font-size: .7rem; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; }
.seo-summary strong { display: block; font-size: clamp(1.4rem, 2.4vw, 2rem); line-height: 1.2; }
.seo-summary p { color: #b9c3d8; line-height: 1.7; }
.seo-summary a { display: inline-flex; margin-top: .4rem; color: #fff; font-weight: 800; text-decoration: underline; text-decoration-color: var(--amber); text-underline-offset: 5px; }
.seo-section { padding: clamp(4rem, 7vw, 6.5rem) 0; }
.seo-section-alt { background: #fff; }
.seo-section-heading { max-width: 760px; margin-bottom: 2rem; }
.seo-section-heading h2, .seo-two-column h2, .seo-campus-note h2, .seo-closing h2 { margin: 0; font-size: clamp(2rem, 4vw, 3.7rem); line-height: 1.05; letter-spacing: -.045em; }
.seo-section-heading > p:last-child, .seo-two-column > div > p, .seo-campus-note > div > p { color: var(--muted); line-height: 1.8; }
.seo-topic-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
.seo-topic-card { display: flex; gap: .85rem; align-items: center; min-height: 86px; padding: 1.2rem; border: 1px solid var(--line); border-radius: 18px; background: #fff; font-weight: 800; box-shadow: 0 12px 36px rgba(10,16,32,.05); }
.seo-topic-card i { display: grid; width: 30px; height: 30px; place-items: center; flex: 0 0 auto; border-radius: 50%; background: rgba(24,201,154,.12); color: var(--mint); }
.seo-two-column { display: grid; grid-template-columns: 1fr .9fr; gap: clamp(2.5rem, 7vw, 6rem); align-items: center; }
.seo-process { display: grid; gap: 1rem; }
.seo-process article { display: grid; grid-template-columns: auto 1fr; gap: 1rem; padding: 1.25rem; border: 1px solid var(--line); border-radius: 18px; background: var(--paper); }
.seo-process article > span { display: grid; width: 42px; height: 42px; place-items: center; border-radius: 13px; background: var(--ink); color: var(--amber); font-size: .72rem; font-weight: 900; }
.seo-process strong { font-size: 1rem; }
.seo-process p { margin: .25rem 0 0; color: var(--muted); line-height: 1.55; }
.seo-campus-note { display: grid; grid-template-columns: 1.15fr .85fr; gap: 2rem; align-items: center; }
.seo-independent { display: flex; gap: 1rem; padding: 1.4rem; border: 1px solid var(--line); border-radius: 20px; background: #fff; color: var(--muted); }
.seo-independent i { color: var(--blue); font-size: 1.35rem; }
.seo-independent p { margin: 0; line-height: 1.65; font-size: .88rem; }
.seo-related-section { background: #eef1f8; }
.seo-related-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
.seo-related-card { position: relative; display: flex; min-height: 170px; flex-direction: column; justify-content: space-between; padding: 1.4rem; border: 1px solid var(--line); border-radius: 20px; background: #fff; transition: transform 180ms ease, box-shadow 180ms ease; }
.seo-related-card:hover { transform: translateY(-4px); box-shadow: var(--shadow); }
.seo-related-card span { color: var(--blue); font-size: .7rem; font-weight: 900; text-transform: uppercase; }
.seo-related-card strong { padding-right: 2rem; font-size: 1.06rem; line-height: 1.35; }
.seo-related-card i { position: absolute; right: 1.3rem; bottom: 1.35rem; color: var(--blue); }
.seo-faq-wrap { display: grid; grid-template-columns: .7fr 1.3fr; gap: clamp(2rem, 7vw, 6rem); }
.seo-faq details { border-top: 1px solid var(--line); padding: 1.15rem 0; }
.seo-faq details:last-child { border-bottom: 1px solid var(--line); }
.seo-faq summary { cursor: pointer; font-weight: 900; }
.seo-faq p { margin: .8rem 0 0; color: var(--muted); line-height: 1.7; }
.seo-closing { padding: clamp(3.5rem, 6vw, 5.5rem) 0; background: var(--ink); color: #fff; }
.seo-closing-inner { display: flex; gap: 2rem; align-items: center; justify-content: space-between; }
.seo-closing-inner > div { max-width: 760px; }
.seo-closing p:not(.seo-kicker) { color: #aeb6c8; line-height: 1.7; }
.seo-closing a { display: inline-flex; min-height: 54px; gap: .7rem; align-items: center; flex: 0 0 auto; padding: .9rem 1.2rem; border-radius: 14px; background: var(--amber); color: var(--ink); font-weight: 900; }
@media (max-width: 850px) {
  .seo-hero-grid, .seo-two-column, .seo-campus-note, .seo-faq-wrap { grid-template-columns: 1fr; }
  .seo-topic-grid, .seo-related-grid { grid-template-columns: repeat(2, 1fr); }
  .seo-summary { max-width: 640px; }
  .seo-closing-inner { align-items: flex-start; flex-direction: column; }
}
@media (max-width: 560px) {
  .seo-shell { width: min(100% - 28px, 1160px); }
  .seo-hero { padding: 3.5rem 0 4rem; }
  .seo-topic-grid, .seo-related-grid { grid-template-columns: 1fr; }
  .seo-actions { align-items: stretch; flex-direction: column; }
  .seo-primary, .seo-secondary { width: 100%; }
  .seo-breadcrumbs { flex-wrap: wrap; }
}
</style>
